import {
  Box,
  Container,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

// --- ICONS ---
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn"; // Returns
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"; // Service Fee
import BalanceIcon from "@mui/icons-material/Balance"; // Governing Law
import CircleIcon from "@mui/icons-material/Circle";
import ContactSupportIcon from "@mui/icons-material/ContactSupport"; // Contact
import CopyrightIcon from "@mui/icons-material/Copyright"; // IP
import CreditCardOffIcon from "@mui/icons-material/CreditCardOff"; // Refunds & Fees
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GavelIcon from "@mui/icons-material/Gavel"; // Liability
import HandshakeIcon from "@mui/icons-material/Handshake"; // Purchasing Assistance
import HowToRegIcon from "@mui/icons-material/HowToReg"; // Eligibility
import LocalShippingIcon from "@mui/icons-material/LocalShipping"; // Shipping
import PriceCheckIcon from "@mui/icons-material/PriceCheck"; // Price Accuracy
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout"; // Orders
import UpdateIcon from "@mui/icons-material/Update"; // Changes
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser"; // Acceptable Use

// --- HOOKS ---
import { useBreakpoint } from "../../../hooks/useBreakpoint";
import { useMetaTags } from "../../../hooks/useMetaTags";

// --- CUSTOM SCROLL SPY HOOK ---
const useScrollSpy = (ids: string[], offset = 150) => {
  const [activeId, setActiveId] = useState<string>(ids[0]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + offset;
      const currentSection = ids.reduce((acc, id) => {
        const element = document.getElementById(id);
        if (element && element.offsetTop <= scrollPosition) {
          return id;
        }
        return acc;
      }, ids[0]);
      setActiveId(currentSection);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [ids, offset]);
  return activeId;
};

// --- STYLED COMPONENTS ---
const SectionTitle = ({
  children,
  icon: Icon,
}: {
  children: React.ReactNode;
  icon?: React.ElementType;
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, mt: 1 }}>
    {Icon && <Icon sx={{ color: "#fa8900", fontSize: 28 }} />}
    <Typography
      variant="h5"
      sx={{
        fontWeight: 700,
        color: "text.primary",
        fontSize: { xs: "1.2rem", md: "1.4rem" },
      }}
    >
      {children}
    </Typography>
  </Box>
);

const SectionText = ({
  children,
  sx,
}: {
  children: React.ReactNode;
  sx?: any;
}) => (
  <Typography
    variant="body1"
    paragraph
    sx={{
      fontSize: { xs: "0.875rem", sm: "1rem" },
      lineHeight: 1.7,
      color: "text.secondary",
      ...sx,
    }}
  >
    {children}
  </Typography>
);

const BulletList = ({ items }: { items: string[] | any }) => {
  if (!items || !Array.isArray(items)) {
    return null;
  }

  return (
    <List dense sx={{ pl: 1 }}>
      {items.map((item: string, index: number) => (
        <ListItem key={index} alignItems="flex-start" sx={{ px: 0 }}>
          <ListItemIcon sx={{ minWidth: 24, mt: 1 }}>
            <CircleIcon sx={{ fontSize: 6, color: "text.secondary" }} />
          </ListItemIcon>
          <ListItemText
            primary={<span dangerouslySetInnerHTML={{ __html: item }} />}
            primaryTypographyProps={{
              variant: "body1",
              color: "text.secondary",
              fontSize: "0.95rem",
              component: "span",
            }}
          />
        </ListItem>
      ))}
    </List>
  );
};

const TermsAndCondition: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const { isMobile } = useBreakpoint();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const SECTIONS = useMemo(
    () => [
      {
        id: "eligibility",
        title: t("termsPage.sections.eligibility"),
        icon: HowToRegIcon,
      },
      {
        id: "price-accuracy",
        title: t("termsPage.sections.priceAccuracy"),
        icon: PriceCheckIcon,
      },
      {
        id: "orders",
        title: t("termsPage.sections.orders"),
        icon: ShoppingCartCheckoutIcon,
      },
      {
        id: "purchasing-assistance",
        title: t("termsPage.sections.purchasingAssistance"),
        icon: HandshakeIcon,
      },
      {
        id: "service-fee",
        title: t("termsPage.sections.serviceFee"),
        icon: AttachMoneyIcon,
      },
      {
        id: "shipping",
        title: t("termsPage.sections.shipping"),
        icon: LocalShippingIcon,
      },
      {
        id: "returns",
        title: t("termsPage.sections.returns"),
        icon: AssignmentReturnIcon,
      },
      {
        id: "refunds-fees",
        title: t("termsPage.sections.refundsFees"),
        icon: CreditCardOffIcon,
      },
      {
        id: "acceptable-use",
        title: t("termsPage.sections.acceptableUse"),
        icon: VerifiedUserIcon,
      },
      {
        id: "intellectual",
        title: t("termsPage.sections.intellectual"),
        icon: CopyrightIcon,
      },
      {
        id: "liability",
        title: t("termsPage.sections.liability"),
        icon: GavelIcon,
      },
      {
        id: "governing",
        title: t("termsPage.sections.governing"),
        icon: BalanceIcon,
      },
      {
        id: "changes",
        title: t("termsPage.sections.changes"),
        icon: UpdateIcon,
      },
      {
        id: "contact",
        title: t("termsPage.sections.contact"),
        icon: ContactSupportIcon,
      },
    ],
    [t]
  );

  const sectionIds = useMemo(() => SECTIONS.map((s) => s.id), [SECTIONS]);
  const activeSection = useScrollSpy(sectionIds);

  useMetaTags({
    title: t("termsPage.meta.title"),
    description: t("termsPage.meta.description"),
    keywords: t("termsPage.meta.keywords"),
  });

  const scrollToSection = useCallback(
    (id: string, behavior: ScrollBehavior = "smooth") => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 140;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior });
      setMobileMenuOpen(false);
    }
    },
    []
  );

  useEffect(() => {
    if (!location.hash) return;
    const targetId = location.hash.replace("#", "");
    if (!targetId) return;
    const timeoutId = window.setTimeout(() => {
      scrollToSection(targetId, "auto");
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [location.hash, scrollToSection]);

  return (
    <Box sx={{ minHeight: "50vh", py: { xs: 3, md: 5 } }}>
      <Container maxWidth="xl" disableGutters sx={{ px: 2 }}>
        {/* --- MOBILE NAV --- */}
        {isMobile && (
          <Paper
            variant="outlined"
            sx={{ mb: 4, p: 2, bgcolor: "grey.50" }}
            className="notranslate"
            translate="no"
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              sx={{ cursor: "pointer" }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                {t("termsPage.onThisPage")}
              </Typography>
              <IconButton size="small">
                <ExpandMoreIcon
                  sx={{
                    transform: mobileMenuOpen
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                />
              </IconButton>
            </Box>

            <Box sx={{ display: mobileMenuOpen ? "block" : "none", mt: 2 }}>
              {SECTIONS.map((section, idx) => (
                <Link
                  key={section.id}
                  component="button"
                  onClick={() => scrollToSection(section.id)}
                  sx={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    py: 1,
                    textDecoration: "none",
                    color:
                      activeSection === section.id
                        ? theme.palette.warning.main
                        : theme.palette.text.secondary,
                    fontWeight: activeSection === section.id ? 700 : 400,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    fontSize: "0.9rem",
                  }}
                >
                  {idx + 1}. {section.title}
                </Link>
              ))}
            </Box>
          </Paper>
        )}

        <Box sx={{ display: "flex", gap: 6 }}>
          {/* --- DESKTOP SIDEBAR --- */}
          {isMdUp && (
            <Box
              sx={{ width: 260, flexShrink: 0 }}
              className="notranslate"
              translate="no"
            >
              <Box sx={{ position: "sticky", top: 140 }}>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    color: "text.secondary",
                    mb: 2,
                  }}
                >
                  {t("termsPage.onThisPage")}
                </Typography>
                <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                  {SECTIONS.map((section) => {
                    const isActive = activeSection === section.id;
                    return (
                      <Box component="li" key={section.id} sx={{ mb: 0 }}>
                        <Link
                          onClick={() => scrollToSection(section.id)}
                          sx={{
                            display: "block",
                            cursor: "pointer",
                            textDecoration: "none",
                            fontSize: "0.875rem",
                            py: 0.75,
                            pl: 2,
                            borderLeft: "3px solid",
                            borderColor: isActive
                              ? "warning.main"
                              : "transparent",
                            color: isActive ? "text.primary" : "text.secondary",
                            fontWeight: isActive ? 600 : 400,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              color: "warning.main",
                              borderColor: "warning.light",
                              bgcolor: "grey.50",
                            },
                          }}
                        >
                          {section.title}
                        </Link>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          )}

          {/* --- MAIN CONTENT --- */}
          <Box
            sx={{ flex: 1, maxWidth: { md: "800px" } }}
            className="notranslate"
            translate="no"
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: "text.primary",
                mb: 2,
                letterSpacing: "-0.5px",
              }}
            >
              {t("termsPage.title")}
            </Typography>

            <SectionText>
              <span
                dangerouslySetInnerHTML={{ __html: t("termsPage.intro") }}
              />
            </SectionText>

            {/* 1. ELIGIBILITY */}
            <Box id="eligibility" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={HowToRegIcon}>
                {t("termsPage.sections.eligibility")}
              </SectionTitle>
              <SectionText>{t("termsPage.eligibility.text1")}</SectionText>
              <SectionText>{t("termsPage.eligibility.text2")}</SectionText>
              <SectionText>{t("termsPage.eligibility.text3")}</SectionText>
            </Box>

            {/* 2. PRICE ACCURACY */}
            <Box id="price-accuracy" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={PriceCheckIcon}>
                {t("termsPage.sections.priceAccuracy")}
              </SectionTitle>
              <SectionText>{t("termsPage.priceAccuracy.text1")}</SectionText>
              <SectionText>{t("termsPage.priceAccuracy.text2")}</SectionText>

              <Paper
                variant="outlined"
                sx={{ p: 3, bgcolor: "grey.50", mb: 3 }}
              >
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  {t("termsPage.priceAccuracy.handleTitle")}
                </Typography>
                <BulletList
                  items={t("termsPage.priceAccuracy.handleList", {
                    returnObjects: true,
                  })}
                />
              </Paper>

              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                {t("termsPage.priceAccuracy.commitmentTitle")}
              </Typography>
              <SectionText>
                {t("termsPage.priceAccuracy.commitmentText")}
              </SectionText>
            </Box>

            {/* 3. ORDERS & PAYMENTS */}
            <Box id="orders" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={ShoppingCartCheckoutIcon}>
                {t("termsPage.sections.orders")}
              </SectionTitle>
              <BulletList
                items={t("termsPage.orders.list", { returnObjects: true })}
              />
            </Box>

            {/* 4. PURCHASING ASSISTANCE & PRODUCT OWNERSHIP */}
            <Box id="purchasing-assistance" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={HandshakeIcon}>
                {t("termsPage.sections.purchasingAssistance")}
              </SectionTitle>
              <SectionText>{t("termsPage.purchasingAssistance.text1")}</SectionText>
              <SectionText>{t("termsPage.purchasingAssistance.text2")}</SectionText>
              <SectionText>{t("termsPage.purchasingAssistance.text3")}</SectionText>
              <SectionText>{t("termsPage.purchasingAssistance.text4")}</SectionText>
            </Box>

            {/* 5. SERVICE FEE */}
            <Box id="service-fee" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={AttachMoneyIcon}>
                {t("termsPage.sections.serviceFee")}
              </SectionTitle>
              <SectionText>{t("termsPage.serviceFee.text1")}</SectionText>
              <SectionText>{t("termsPage.serviceFee.text2")}</SectionText>
              <SectionText>{t("termsPage.serviceFee.text3")}</SectionText>
              <BulletList
                items={t("termsPage.serviceFee.list", { returnObjects: true })}
              />
              <SectionText>{t("termsPage.serviceFee.text4")}</SectionText>
              <SectionText>{t("termsPage.serviceFee.text5")}</SectionText>
            </Box>

            {/* 6. SHIPPING */}
            <Box id="shipping" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={LocalShippingIcon}>
                {t("termsPage.sections.shipping")}
              </SectionTitle>
              <SectionText>{t("termsPage.shipping.text1")}</SectionText>
              <SectionText>{t("termsPage.shipping.text2")}</SectionText>
              <SectionText>{t("termsPage.shipping.text3")}</SectionText>
            </Box>

            {/* 7. RETURNS */}
            <Box id="returns" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={AssignmentReturnIcon}>
                {t("termsPage.sections.returns")}
              </SectionTitle>
              <SectionText>{t("termsPage.returns.text1")}</SectionText>
              <SectionText>{t("termsPage.returns.text2")}</SectionText>
            </Box>

            {/* 8. REFUNDS & FEES */}
            <Box id="refunds-fees" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={CreditCardOffIcon}>
                {t("termsPage.sections.refundsFees")}
              </SectionTitle>
              <SectionText>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t("termsPage.refundsFees.text1"),
                  }}
                />
              </SectionText>
              <SectionText>{t("termsPage.refundsFees.text2")}</SectionText>
              <SectionText>{t("termsPage.refundsFees.text3")}</SectionText>
            </Box>

            {/* 9. ACCEPTABLE USE */}
            <Box id="acceptable-use" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={VerifiedUserIcon}>
                {t("termsPage.sections.acceptableUse")}
              </SectionTitle>
              <SectionText>{t("termsPage.acceptableUse.intro")}</SectionText>
              <BulletList
                items={t("termsPage.acceptableUse.list", {
                  returnObjects: true,
                })}
              />
            </Box>

            {/* 10. INTELLECTUAL PROPERTY */}
            <Box id="intellectual" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={CopyrightIcon}>
                {t("termsPage.sections.intellectual")}
              </SectionTitle>
              <SectionText>{t("termsPage.intellectual.text1")}</SectionText>
              <SectionText>{t("termsPage.intellectual.text2")}</SectionText>
            </Box>

            {/* 11. LIABILITY */}
            <Box id="liability" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={GavelIcon}>
                {t("termsPage.sections.liability")}
              </SectionTitle>
              <SectionText>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t("termsPage.liability.text1"),
                  }}
                />
              </SectionText>
              <SectionText>{t("termsPage.liability.text2")}</SectionText>
              <SectionText>{t("termsPage.liability.text3")}</SectionText>
            </Box>

            {/* 12. GOVERNING LAW */}
            <Box id="governing" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={BalanceIcon}>
                {t("termsPage.sections.governing")}
              </SectionTitle>
              <SectionText>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t("termsPage.governing.text1"),
                  }}
                />
              </SectionText>
              <SectionText>{t("termsPage.governing.text2")}</SectionText>
            </Box>

            {/* 13. CHANGES */}
            <Box id="changes" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={UpdateIcon}>
                {t("termsPage.sections.changes")}
              </SectionTitle>
              <SectionText>{t("termsPage.changes.text")}</SectionText>
            </Box>

            {/* 14. CONTACT */}
            <Box id="contact" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={ContactSupportIcon}>
                {t("termsPage.sections.contact")}
              </SectionTitle>
              <SectionText>{t("termsPage.contact.intro")}</SectionText>

              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  bgcolor: "grey.100",
                  borderRadius: 3,
                  textAlign: "center",
                }}
              >
                <Typography variant="h6" gutterBottom fontWeight={700}>
                  Chai Services LLC
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Platform: US2TJK
                </Typography>

                <Box
                  sx={{
                    display: "inline-block",
                    px: 3,
                    py: 1,
                    bgcolor: "white",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Link
                    href="mailto:support@us2tjk.com"
                    sx={{
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      textDecoration: "none",
                      color: "primary.main",
                    }}
                  >
                    support@us2tjk.com
                  </Link>
                </Box>
              </Paper>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default TermsAndCondition;
